import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { generateAI } from '../lib/api';
import { Sparkles, FileText, TrendingUp, FileCheck, Send, Copy, Loader2 } from 'lucide-react';

const AI_TYPES = [
  { value: 'proposal', label: 'Draft Proposal', icon: FileText, description: 'Generate professional business proposals' },
  { value: 'report', label: 'Generate Report', icon: FileCheck, description: 'Create data-driven business reports' },
  { value: 'summary', label: 'Summarize', icon: TrendingUp, description: 'Summarize documents or data' },
  { value: 'general', label: 'General Assistant', icon: Sparkles, description: 'Ask anything about your business' }
];

export const AIAssistant = () => {
  const [prompt, setPrompt] = useState('');
  const [context, setContext] = useState('');
  const [type, setType] = useState('general');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState('');
  const [history, setHistory] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    setLoading(true);
    try {
      const res = await generateAI(prompt, context, type);
      setResponse(res.data.response);
      setHistory([
        { prompt, context, type, response: res.data.response, timestamp: new Date().toISOString() },
        ...history.slice(0, 9)
      ]);
      toast.success('Response generated');
    } catch (error) {
      toast.error('Failed to generate response. Please check your API key.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(response);
    toast.success('Copied to clipboard');
  };

  const selectedType = AI_TYPES.find(t => t.value === type);

  return (
    <div className="space-y-6" data-testid="ai-assistant-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">AI Assistant</h1>
          <p className="text-muted-foreground mt-1">AI-powered enterprise operations support</p>
        </div>
        <Badge variant="outline" className="px-3 py-1">
          <Sparkles className="h-4 w-4 mr-2 text-amber-500" />
          GPT-4o Powered
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input Section */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                Ask AI Assistant
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Type Selection */}
                <div className="grid grid-cols-4 gap-2">
                  {AI_TYPES.map((t) => {
                    const Icon = t.icon;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setType(t.value)}
                        className={`p-3 rounded-lg border text-left transition-all ${
                          type === t.value 
                            ? 'border-slate-900 bg-slate-50' 
                            : 'border-border hover:border-slate-300'
                        }`}
                        data-testid={`ai-type-${t.value}`}
                      >
                        <Icon className={`h-5 w-5 mb-2 ${type === t.value ? 'text-slate-900' : 'text-muted-foreground'}`} />
                        <p className="text-sm font-medium">{t.label}</p>
                      </button>
                    );
                  })}
                </div>

                {/* Context (Optional) */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Context (Optional)</label>
                  <Textarea
                    value={context}
                    onChange={(e) => setContext(e.target.value)}
                    placeholder="Paste relevant data, document content, or background information..."
                    rows={3}
                    className="resize-none"
                    data-testid="ai-context-input"
                  />
                </div>

                {/* Prompt */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Your Request *</label>
                  <Textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={
                      type === 'proposal' ? "Describe the proposal you need (e.g., 'Write a proposal for a software development project worth R500,000...')" :
                      type === 'report' ? "What report do you need? (e.g., 'Generate a monthly sales performance report...')" :
                      type === 'summary' ? "What would you like summarized? (e.g., 'Summarize the following meeting notes...')" :
                      "Ask anything about your business operations..."
                    }
                    rows={4}
                    className="resize-none"
                    required
                    data-testid="ai-prompt-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading || !prompt.trim()}
                  data-testid="ai-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Generate Response
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Response */}
          {response && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">AI Response</CardTitle>
                  <Button variant="ghost" size="sm" onClick={copyToClipboard} data-testid="copy-response-btn">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div 
                  className="prose prose-slate max-w-none whitespace-pre-wrap text-sm leading-relaxed"
                  data-testid="ai-response-content"
                >
                  {response}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Tips */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Tips for {selectedType?.label}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              {type === 'proposal' && (
                <ul className="space-y-2 list-disc pl-4">
                  <li>Include project scope and objectives</li>
                  <li>Mention budget range if known</li>
                  <li>Specify timeline requirements</li>
                  <li>Include client background if relevant</li>
                </ul>
              )}
              {type === 'report' && (
                <ul className="space-y-2 list-disc pl-4">
                  <li>Specify the reporting period</li>
                  <li>Mention key metrics to highlight</li>
                  <li>Include data in context field</li>
                  <li>Specify format (executive, detailed)</li>
                </ul>
              )}
              {type === 'summary' && (
                <ul className="space-y-2 list-disc pl-4">
                  <li>Paste full content in context</li>
                  <li>Specify summary length needed</li>
                  <li>Mention key points to focus on</li>
                  <li>Indicate target audience</li>
                </ul>
              )}
              {type === 'general' && (
                <ul className="space-y-2 list-disc pl-4">
                  <li>Be specific in your questions</li>
                  <li>Provide relevant context</li>
                  <li>Ask follow-up questions</li>
                  <li>Use for analysis and advice</li>
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Recent History */}
          {history.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Recent Queries</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {history.slice(0, 5).map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setPrompt(item.prompt);
                      setContext(item.context);
                      setType(item.type);
                      setResponse(item.response);
                    }}
                    className="w-full text-left p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="outline" className="text-xs">{item.type}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(item.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <p className="text-sm truncate">{item.prompt}</p>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Example Prompts */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Example Prompts</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {[
                { type: 'proposal', text: 'Write a proposal for IT training services' },
                { type: 'report', text: 'Generate a quarterly sales summary' },
                { type: 'summary', text: 'Summarize project progress report' },
                { type: 'general', text: 'What are best practices for grant compliance?' }
              ].map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setType(example.type);
                    setPrompt(example.text);
                  }}
                  className="w-full text-left p-2 rounded text-sm hover:bg-muted transition-colors"
                >
                  <Badge variant="outline" className="text-xs mr-2">{example.type}</Badge>
                  {example.text}
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
